<script lang="ts" setup>
import { debounce } from 'lodash/fp';
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import persistanceHelper from '@exchange/helpers/persistance-helper';
import useMQBreakpoints from '@exchange/libs/composables/shared/src/lib/useMQBreakpoints';

import { tabs as bottomNavTabs, type Tabs, type TabKeys, type TabValues } from './components/bottom-tabs';
import ExchangeBottomTabs from './components/ExchangeBottomTabs.vue';
import AppHeader from '../app-header/AppHeader.vue';

const props = defineProps({
  marketId: { type: String },
});

const route = useRoute();
const router = useRouter();

const { isXS } = useMQBreakpoints();

const EXCHANGE_NAV_TAB = '1T_NAV_TAB';
const DEFAULT_NAV_TAB_KEY = bottomNavTabs.MARKETS.key;

const isFutures = computed(() => route.name === 'futures');
const isSpot = computed(() => route.name === 'spot');

const getInitMobileActiveTab = () => {
  if (isSpot.value) {
    return bottomNavTabs.MARKETS.key;
  }

  const tab = Object.values(bottomNavTabs).find((e) => e.routeName === route.name)?.key;

  return tab ?? persistanceHelper.getObjFromJSON(EXCHANGE_NAV_TAB, DEFAULT_NAV_TAB_KEY);
};

const mobileSlideMoveBack = ref(true);
const mobileActiveTab = ref<TabKeys>(getInitMobileActiveTab());

const getBottomTabs = (tabs: typeof bottomNavTabs | Omit<typeof bottomNavTabs, 'FUTURES'>) =>
  (Object.keys(tabs) as Array<Tabs>).reduce(
    (acc, cur) => {
      const t = bottomNavTabs[cur];

      acc[t.key] = { ...t };

      return acc;
    },
    {} as Record<TabKeys, TabValues>,
  );

const bottomTabs = computed(() => getBottomTabs(bottomNavTabs));

const onBottomTabClicked = (tab: TabValues) => {
  const activeTabOrder = bottomTabs.value[mobileActiveTab.value]?.order;
  const newTabOrder = bottomTabs.value[tab.key]?.order;

  mobileSlideMoveBack.value = activeTabOrder !== undefined && newTabOrder !== undefined && activeTabOrder > newTabOrder;
  mobileActiveTab.value = tab.key;
  persistanceHelper.localstorageSet(EXCHANGE_NAV_TAB, tab.key);

  router.push({
    name: tab.routeName,
    hash: route.hash,
    query: route.query,
  });
};

const onMarketClicked = debounce(200, (marketId: string) => {
  const isFuturesMarket = marketId.endsWith('_P');
  const navigateToMarket = () =>
    router.push({
      name: isFuturesMarket ? 'futures' : 'spot',
      params: {
        marketId,
      },
    });

  if (isXS.value) {
    navigateToMarket();

    return;
  }

  if (marketId !== props.marketId) {
    navigateToMarket();
  }
});

const onMobileContentBack = () => {
  onBottomTabClicked(bottomNavTabs.MARKETS);
};

watch(route, (nV, oV) => {
  if (nV.name === oV.name) {
    return;
  }

  if (route.name && isXS.value) {
    const tab = Object.values(bottomNavTabs).find((e) => e.routeName === route.name);

    if (tab) {
      onBottomTabClicked(tab);
    }
  }
});
</script>

<template>
  <div
    v-if="isXS"
    class="trading-layout-mobile"
  >
    <router-view v-slot="{ Component }">
      <x-transition-slide
        :push="true"
        :move-back="mobileSlideMoveBack"
        :is-xs="true"
      >
        <component
          :is="Component"
          :market-id="marketId"
          :class="{ 'trading-layout-mobile__content': !(isFutures || isSpot) }"
          @mobile:go-back="onMobileContentBack"
          @market-clicked="onMarketClicked"
        />
      </x-transition-slide>
    </router-view>
    <exchange-bottom-tabs
      :bottom-tabs="bottomTabs"
      :active-tab="mobileActiveTab"
      @bottom-tab:clicked="onBottomTabClicked"
    />
  </div>
  <template v-else>
    <app-header />
    <router-view v-slot="{ Component }">
      <component
        :is="Component"
        :market-id="marketId"
        @market-clicked="onMarketClicked"
      />
    </router-view>
  </template>
</template>

<style lang="scss">
.trading-layout-mobile {
  --x-transition-slide-background-color: rgb(var(--v-theme-elevation-1));

  display: flex;
  height: 100%;
  flex-direction: column;
  backface-visibility: hidden;
  pointer-events: auto;
  user-select: none;

  &__content {
    padding: 20px var(--padding-x-xs) 0;
  }
}
</style>
